"use client"

import { useState, useEffect } from "react"
import { Card, Button, Form, Row, Col, Container, Toast, Offcanvas } from "react-bootstrap"
import { Grid, List, Filter } from "react-feather"
import { useNavigate, useLocation, useSearchParams } from "react-router-dom"
import { useCart } from "../context/CartContext"
import { useWishlist } from "../context/WishlistContext"
import { ToastContainer } from "react-bootstrap"
import { FaRegHeart, FaHeart } from "react-icons/fa"
import axios from "axios"

const primaryStyle = {
  backgroundColor: "#25A1D5",
  borderColor: "#25A1D5",
}

const FilterProducts = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { addToCart } = useCart()
  const { addToWishlist, removeFromWishlist, wishlist: contextWishlist } = useWishlist()

  // Get category and brand filters from state/query
  const categoryFilterFromState = location.state?.categoryFilter
  const categoryFilterFromParams = searchParams.get("category")
  const initialCategoryFilter = categoryFilterFromState || categoryFilterFromParams
  const brandFilterFromState = location.state?.brandFilter

  const [selectedFilters, setSelectedFilters] = useState({
    brand: brandFilterFromState ? [brandFilterFromState] : [],
    categories: initialCategoryFilter ? [initialCategoryFilter] : [],
    price: [],
    features: [],
  })
  const [gridView, setGridView] = useState(true)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [brands, setBrands] = useState([])
  const [loading, setLoading] = useState(true)

  // Dynamic filters based on fetched data
  const [filters, setFilters] = useState([
    {
      id: "price",
      name: "Price",
      options: [
        { value: "under-20000", label: "Under ₹20,000" },
        { value: "20000-30000", label: "₹20,000 - ₹30,000" },
        { value: "30000-50000", label: "₹30,000 - ₹50,000" },
        { value: "50000-75000", label: "₹50,000 - ₹75,000" },
        { value: "75000-100000", label: "₹75,000 - ₹1,00,000" },
        { value: "100000-150000", label: "₹1,00,000 - ₹1,50,000" },
        { value: "150000-200000", label: "₹1,50,000 - ₹2,00,000" },
        { value: "over-200000", label: "Over ₹2,00,000" },
      ],
    },
    {
      id: "features",
      name: "Features",
      options: [
        { value: "stitching", label: "Stitching" },
        { value: "overlocking", label: "Overlocking" },
        { value: "embroidery", label: "Embroidery" },
        { value: "zigzag", label: "Zigzag" },
        { value: "buttonhole", label: "Buttonhole" },
        { value: "automatic needle threader", label: "Automatic Needle Threader" },
        { value: "speed control", label: "Speed Control" },
        { value: "heavy duty", label: "Heavy Duty" },
        { value: "quilting", label: "Quilting" },
        { value: "lightweight", label: "Lightweight" },
        { value: "industrial", label: "Industrial" },
      ],
    },
  ])

  // Fetch categories, brands, and products
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const categoriesResponse = await axios.get("https://madhusewingmachines.com/api/categories/getcategory")
        setCategories(categoriesResponse.data)

        // Fetch brands
        const brandsResponse = await axios.get("https://madhusewingmachines.com/api/brands")
        setBrands(brandsResponse.data.brands || [])

        // Fetch products
        const productsResponse = await axios.get("https://madhusewingmachines.com/api/products")
        setProducts(productsResponse.data.products)

        // Update filters with dynamic data
        const dynamicFilters = [
          {
            id: "categories",
            name: "Categories",
            options: categoriesResponse.data.map((category) => ({
              value: category.name, // Use category name to match with product.type
              label: category.name,
            })),
          },
          {
            id: "brand",
            name: "Brand",
            options: (brandsResponse.data.brands || []).map((brand) => ({
              value: brand.name,
              label: brand.name,
            })),
          },
          {
            id: "price",
            name: "Price",
            options: [
              { value: "under-20000", label: "Under ₹20,000" },
              { value: "20000-30000", label: "₹20,000 - ₹30,000" },
              { value: "30000-50000", label: "₹30,000 - ₹50,000" },
              { value: "50000-75000", label: "₹50,000 - ₹75,000" },
              { value: "75000-100000", label: "₹75,000 - ₹1,00,000" },
              { value: "100000-150000", label: "₹1,00,000 - ₹1,50,000" },
              { value: "150000-200000", label: "₹1,50,000 - ₹2,00,000" },
              { value: "over-200000", label: "Over ₹2,00,000" },
            ],
          },
          {
            id: "features",
            name: "Features",
            options: [
              { value: "stitching", label: "Stitching" },
              { value: "overlocking", label: "Overlocking" },
              { value: "embroidery", label: "Embroidery" },
              { value: "zigzag", label: "Zigzag" },
              { value: "buttonhole", label: "Buttonhole" },
              { value: "automatic needle threader", label: "Automatic Needle Threader" },
              { value: "speed control", label: "Speed Control" },
              { value: "heavy duty", label: "Heavy Duty" },
              { value: "quilting", label: "Quilting" },
              { value: "lightweight", label: "Lightweight" },
              { value: "industrial", label: "Industrial" },
            ],
          },
        ]

        setFilters(dynamicFilters)
        setLoading(false)
      } catch (error) {
        console.error("Error fetching data:", error)
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const filteredProducts = products.filter((product) => {
    return Object.entries(selectedFilters).every(([filterType, selectedValues]) => {
      if (!selectedValues || selectedValues.length === 0) return true
      switch (filterType) {
        case "categories": {
          // Flexible match: ignore case, spaces, punctuation; allow partial contains either way
          const normalize = (s) => (s || "").toString().trim().toLowerCase().replace(/[^a-z0-9]/g, "")
          const productTypeNorm = normalize(product.type)
          return selectedValues.some((val) => {
            const selNorm = normalize(val)
            if (!productTypeNorm || !selNorm) return false
            return productTypeNorm === selNorm || productTypeNorm.includes(selNorm) || selNorm.includes(productTypeNorm)
          })
        }
        case "brand":
          return selectedValues.some((val) => (product.brand || "").trim().toLowerCase() === (val || "").trim().toLowerCase())
        case "price":
          const price = product.price
          return selectedValues.some((range) => {
            switch (range) {
              case "under-20000":
                return price < 20000
              case "20000-30000":
                return price >= 20000 && price < 30000
              case "30000-50000":
                return price >= 30000 && price < 50000
              case "50000-75000":
                return price >= 50000 && price < 75000
              case "75000-100000":
                return price >= 75000 && price < 100000
              case "100000-150000":
                return price >= 100000 && price < 150000
              case "150000-200000":
                return price >= 150000 && price < 200000
              case "over-200000":
                return price >= 200000
              default:
                return true
            }
          })
        case "features": {
          const normalize = (s) => (s || "").toString().trim().toLowerCase()
          const productFeatures = (product.features || []).map((f) => normalize(f))
          return selectedValues.some((sel) => {
            const selNorm = normalize(sel)
            return productFeatures.some((pf) => pf === selNorm || pf.includes(selNorm) || selNorm.includes(pf))
          })
        }
        default:
          return true
      }
    })
  })

  const handleFilterChange = (filterId, value) => {
    setSelectedFilters((prevFilters) => {
      const currentValues = prevFilters[filterId] || []
      if (currentValues.includes(value)) {
        return {
          ...prevFilters,
          [filterId]: currentValues.filter((v) => v !== value),
        }
      } else {
        return {
          ...prevFilters,
          [filterId]: [...currentValues, value],
        }
      }
    })
  }

  const clearFilters = () => {
    setSelectedFilters({
      brand: [],
      categories: [],
      price: [],
      features: [],
    })
    navigate("/productpage", { replace: true })
  }

  const clearCategoryFilter = () => {
    setSelectedFilters((prev) => ({
      ...prev,
      categories: [],
    }))
    navigate("/productpage", { replace: true })
  }

  const handleAddToCart = (product) => {
    addToCart(product)
    setToastMessage("🔥 Successfully added to Cart!")
    setShowToast(true)
  }

  const handleAddToWishlist = (product) => {
    const isInWishlist = contextWishlist.some((item) => item._id === product._id)

    if (isInWishlist) {
      removeFromWishlist(product._id)
      setToastMessage("❌ Removed from Wishlist!")
    } else {
      addToWishlist(product)
      setToastMessage("❤️ Successfully added to Wishlist!")
    }

    setShowToast(true)
  }

  const isProductInWishlist = (productId) => {
    return contextWishlist.some((item) => item._id === productId)
  }

  const handleProductClick = (id) => {
    navigate(`/product/${id}`)
  }

  // Get current category name for display
  const getCurrentCategoryName = () => {
    if (!initialCategoryFilter) return null
    const byName = categories.find((cat) => (cat.name || "").toLowerCase() === (initialCategoryFilter || "").toLowerCase())
    if (byName) return byName.name
    const byId = categories.find((cat) => cat._id === initialCategoryFilter)
    return byId ? byId.name : initialCategoryFilter
  }

  if (loading) {
    return (
      <Container
        fluid
        className="p-4 d-flex justify-content-center align-items-center"
        style={{
          background: "linear-gradient(135deg, #e6f7ff, #f0f0f0)",
          minHeight: "100vh",
          marginTop: "90px",
        }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </Container>
    )
  }

  return (
    <Container
      fluid
      className="p-4"
      style={{
        background: "linear-gradient(135deg, #e6f7ff, #f0f0f0)",
        minHeight: "100vh",
        marginTop: "90px",
      }}
    >
      <ToastContainer
        position="top-center"
        className="p-3"
        style={{
          position: "fixed",
          zIndex: 1050,
          top: "80px",
          left: "50%",
          transform: "translateX(-50%)",
        }}
      >
        <Toast show={showToast} onClose={() => setShowToast(false)} delay={2000} autohide bg="success" text="white">
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      <Button variant="primary" className="d-md-none mb-2" onClick={() => setShowMobileFilters(true)}>
        <Filter size={20} /> Filters
      </Button>

      <Row>
        <Col md={3} className="border p-3 bg-light rounded mb-4 mb-md-0 d-none d-md-block">
          <h4>Filter Products</h4>
          <Button variant="outline-secondary" className="w-100 mb-3" onClick={clearFilters}>
            Clear All Filters
          </Button>

          {/* Show current category filter if it exists */}
          {initialCategoryFilter && (
            <div className="mb-3 p-2 bg-info text-white rounded">
              <h6 className="mb-1">Filtered by Category:</h6>
              <div className="d-flex justify-content-between align-items-center">
                <span>{getCurrentCategoryName()}</span>
                <Button variant="outline-light" size="sm" onClick={clearCategoryFilter}>
                  ×
                </Button>
              </div>
            </div>
          )}

          {filters.map((filter) => (
            <div key={filter.id} className="mb-3">
              <h6>{filter.name}</h6>
              {filter.options.map((option) => (
                <Form.Check
                  key={option.value}
                  type="checkbox"
                  label={option.label}
                  onChange={() => handleFilterChange(filter.id, option.value)}
                  checked={selectedFilters[filter.id]?.includes(option.value)}
                />
              ))}
            </div>
          ))}
        </Col>

        <Offcanvas show={showMobileFilters} onHide={() => setShowMobileFilters(false)} placement="start">
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>Filter Products</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Button variant="outline-secondary" className="w-100 mb-3" onClick={clearFilters}>
              Clear All Filters
            </Button>

            {initialCategoryFilter && (
              <div className="mb-3 p-2 bg-info text-white rounded">
                <h6 className="mb-1">Filtered by Category:</h6>
                <div className="d-flex justify-content-between align-items-center">
                  <span>{getCurrentCategoryName()}</span>
                  <Button variant="outline-light" size="sm" onClick={clearCategoryFilter}>
                    ×
                  </Button>
                </div>
              </div>
            )}

            {filters.map((filter) => (
              <div key={filter.id} className="mb-3">
                <h6>{filter.name}</h6>
                {filter.options.map((option) => (
                  <Form.Check
                    key={option.value}
                    type="checkbox"
                    label={option.label}
                    onChange={() => handleFilterChange(filter.id, option.value)}
                    checked={selectedFilters[filter.id]?.includes(option.value)}
                  />
                ))}
              </div>
            ))}
          </Offcanvas.Body>
        </Offcanvas>

        <Col md={9}>
          <Row className="align-items-center mb-3 d-none d-md-flex">
            <Col>Items per page: {filteredProducts.length}</Col>
            <Col>
              <Form.Select>
                <option>Sort by: Latest</option>
                <option>Most Popular</option>
              </Form.Select>
            </Col>
            <Col className="text-end">
              <Button
                variant={gridView ? "primary" : "outline-secondary"}
                style={gridView ? primaryStyle : {}}
                onClick={() => setGridView(true)}
              >
                <Grid size={20} />
              </Button>
              <Button
                variant={!gridView ? "primary" : "outline-secondary"}
                style={!gridView ? primaryStyle : {}}
                onClick={() => setGridView(false)}
                className="ms-2"
              >
                <List size={20} />
              </Button>
            </Col>
          </Row>

          {filteredProducts.length === 0 ? (
            <div className="text-center py-5">
              <h4>No products found matching your filters</h4>
              <Button variant="primary" onClick={clearFilters}>
                Clear Filters
              </Button>
            </div>
          ) : (
            <Row className={gridView ? "row-cols-1 row-cols-md-4 g-4" : "row-cols-1 g-0"}>
              {filteredProducts.map((product) => (
                <Col key={product._id} className="d-flex justify-content-center">
                  <Card
                    className="shadow-sm border-0 text-center"
                    style={{
                      width: "100%",
                      maxWidth: "470px",
                    }}
                  >
                    <div className="position-relative">
                      <Card.Img
                        variant="top"
                        src={`https://madhusewingmachines.com/uploads/${product.images[0]?.url}`}
                        alt={product.name}
                        className="rounded-top img-fluid"
                        style={{
                          maxHeight: "180px",
                          objectFit: "contain",
                          cursor: "pointer",
                        }}
                        onClick={() => handleProductClick(product._id)}
                      />
                      <button
                        onClick={() => handleAddToWishlist(product)}
                        className="position-absolute top-0 end-0 m-2 p-1 rounded-circle bg-light"
                        style={{ zIndex: 1, border: "none" }}
                      >
                        {isProductInWishlist(product._id) ? (
                          <FaHeart color="red" size={20} />
                        ) : (
                          <FaRegHeart color="black" size={20} />
                        )}
                      </button>
                    </div>
                    <Card.Body>
                      <Card.Title>{product.name}</Card.Title>
                      <Card.Text>
                        <strong>{product.type}</strong>
                        <br />
                       ₹{product.price.toLocaleString()}
                      </Card.Text>
                      <ul className="text-start">
                        {product.features.map((feature, index) => (
                          <li key={index}>{feature}</li>
                        ))}
                      </ul>
                      <Button
                        variant="primary"
                        style={primaryStyle}
                        className="w-100"
                        onClick={() => handleAddToCart(product)}
                      >
                        Add to Cart
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Col>
      </Row>
    </Container>
  )
}

export default FilterProducts
